import * as XLSX from 'xlsx';
import { useAuth } from '../hooks/useAuth';
import DocumentHeader from '../components/DocumentHeader';
import ProductPicker from '../components/ProductPicker';
import ContactPicker from '../components/ContactPicker';
import { Printer, Download } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { reportsApi } from '../api/reports';
import { purchaseApi } from '../api/purchase';
import { coaApi } from '../api/coa';
import { contactsApi } from '../api/contacts';
import { productsApi } from '../api/products';

const typeColor: Record<string,string> = { Asset:'badge-blue', Liability:'badge-red', Equity:'badge-purple', Revenue:'badge-green', Expense:'badge-amber' };

const fmt = (n: any) => Number(n||0).toFixed(2);

const Reports: React.FC = () => {
  const [tab, setTab]         = useState('tb');
  const [from, setFrom]       = useState(new Date().getFullYear() + '-01-01');
  const [to, setTo]           = useState(new Date().getFullYear() + '-12-31');
  const [branchId, setBranchId] = useState('');
  const [contactId, setContactId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [productId, setProductId] = useState('');
  const [productName, setProductName] = useState('');
  const [contactName, setContactName] = useState('');
  const [uaUser, setUaUser] = useState<number|null>(null);
  const [owingOnly, setOwingOnly] = useState(false);
  const { hasPermission } = useAuth();
  const canSeeMargin = hasPermission('sales.view_margin');
  const [lossesOnly, setLossesOnly] = useState(false);
  const [showCorrections, setShowCorrections] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches]   = useState<any[]>([]);
  const [contacts, setContacts]   = useState<any[]>([]);
  const [accounts, setAccounts]   = useState<any[]>([]);
  const [products, setProducts]   = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  useEffect(() => {
    purchaseApi.branches().then(r => setBranches(r.data.data));
    contactsApi.list().then(r => setContacts(r.data.data));
    coaApi.list().then(r => setAccounts(r.data.data));
    productsApi.list().then(r => setProducts(r.data.data));
    purchaseApi.locations().then(r => setLocations(r.data.data));
  }, []);

  const load = () => {
    setLoading(true); setData(null);
    const p = { from, to, branch_id: branchId||undefined, contact_id: contactId||undefined, account_id: accountId||undefined, product_id: productId||undefined, location_id: locationId||undefined };
    const calls: Record<string, any> = {
      tb:   reportsApi.trialBalance(p),
      pl:   reportsApi.profitLoss(p),
      ar:   reportsApi.arAging(p),
      ap:   reportsApi.apAging(p),
      aa:   reportsApi.accountActivity(p),
      ia:   reportsApi.inventoryActivity({ ...p, include_corrections: showCorrections ? 1 : undefined }),
      csummary: reportsApi.customerBalanceSummary(p),
      cdetail:  reportsApi.customerBalanceDetail(p),
      ssummary: reportsApi.supplierBalanceSummary(p),
      sdetail:  reportsApi.supplierBalanceDetail(p),
      cactivity: reportsApi.customerActivity(p),
      sh: reportsApi.salesHistory(p),
      gh: reportsApi.grnHistory(p),
      sactivity: reportsApi.supplierActivity(p),
      iv:   reportsApi.inventoryValuationPivot(p),
      ip:   reportsApi.inventoryPivot(p),
      bs:   reportsApi.balanceSheet({ as_of: to, branch_id: branchId||undefined }),
      iloc: reportsApi.inventoryByLocationPivot(p),
      exp:  reportsApi.expiry({ branch_id: branchId||undefined, days: 90 }),
      sbi:  reportsApi.salesByItem(p),
      sbid: reportsApi.salesByItemDetail(p),
      usm:  reportsApi.unmovedStock(p),
      sbc:  reportsApi.salesByCustomer(p),
      sbcd: reportsApi.salesByCustomerDetail(p),
    };
    calls[tab].then((res: any) => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tab]);
  // eslint-disable-next-line
  useEffect(() => { if (tab === 'ia') load(); }, [showCorrections]);

  const tabs = [
    { key:'sbc', label:'Sales by Customer', group:'Sell' },
    { key:'sbcd', label:'Sales by Customer Detail', group:'Sell' },
    { key:'usm', label:'Un-moved Stock', group:'Inventory' },
    { key:'sh',       label:'Sales History',             group:'Sell' },
    { key:'sbi',      label:'Sales by Item',             group:'Sell' },
    { key:'sbid',     label:'Sales by Item Detail',      group:'Sell' },
    { key:'gh',       label:'GRN History',               group:'Purchase' },
    { key:'bs',       label:'Balance Sheet',             group:'Finance' },
  { key:'tb',       label:'Trial Balance',             group:'Finance' },
    { key:'pl',       label:'Profit & Loss',             group:'Finance' },
    { key:'aa',       label:'Account Activity',          group:'Finance' },
    { key:'ar',       label:'AR Aging',                  group:'Customers' },
    { key:'csummary', label:'Customer Balance Summary',  group:'Customers' },
    { key:'cdetail',  label:'Customer Balance Detail',   group:'Customers' },
    { key:'cactivity', label:'Customer Activity',          group:'Customers' },
    { key:'ap',       label:'AP Aging',                  group:'Suppliers' },
    { key:'ssummary', label:'Supplier Balance Summary',  group:'Suppliers' },
    { key:'sdetail',  label:'Supplier Balance Detail',   group:'Suppliers' },
    { key:'sactivity', label:'Supplier Activity',          group:'Suppliers' },
    { key:'tkt',      label:'Tickets In The Shop',       group:'Repairs' },
  { key:'uact',     label:'User Activity',             group:'Daily' },
  { key:'iv',       label:'Inventory Valuation',       group:'Inventory' },
    { key:'ip',       label:'Stock on Hand',             group:'Inventory' },
    { key:'iloc',     label:'Inventory By Location',     group:'Inventory' },
    { key:'ia',       label:'Inventory Activity',        group:'Inventory' },
    { key:'exp',      label:'Expiry',                    group:'Inventory' },
  ];

  const groups = ['Finance','Sell','Purchase','Customers','Suppliers','Inventory'];
  const sum = data?.summary || {};
  const tb   = Array.isArray(data?.data) ? data.data : [];
  const rev  = Array.isArray(data?.data?.revenue)  ? data.data.revenue  : [];
  const exp  = Array.isArray(data?.data?.expenses) ? data.data.expenses : [];

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
        <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
          <input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} style={{ width:130 }} />
          <span style={{ color:'var(--tx3)', fontSize:11 }}>to</span>
          <input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} style={{ width:130 }} />
          <select className="form-control" value={branchId} onChange={e => setBranchId(e.target.value)} style={{ width:150 }}>
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {(tab==='cactivity'||tab==='sactivity') && (
            <div style={{ width:260 }}>
              <ContactPicker value={contactId} initialLabel={contactName}
                placeholder={tab==='sactivity' ? 'Type to search supplier' : 'Type to search customer'}
                onSelect={(ct:any) => { setContactId(String(ct.id)); setContactName(ct.name); }} />
            </div>
          )}
          {false && <select className="form-control" value={contactId} onChange={e => setContactId(e.target.value)} style={{ width:200 }}>
            <option value="">Select Customer</option>
            {contacts.map((c:any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>}
          <button className="btn btn-primary" onClick={load}>Run</button>
          <button className="btn btn-secondary" onClick={() => window.print()}><Printer size={13} /> PDF / Print</button>
          <button className="btn btn-secondary" onClick={() => {
            const el = document.getElementById('report-content');
            if (!el) return;
            const wb = XLSX.utils.book_new();

            // Capture KPI summary cards first, if any
            const kpiCards = el.querySelectorAll('.kpi-card');
            if (kpiCards.length > 0) {
              const summaryRows: any[][] = [['Metric', 'Value']];
              kpiCards.forEach(card => {
                const label = card.querySelector('.kpi-label')?.textContent || '';
                const value = card.querySelector('.kpi-value')?.textContent || '';
                summaryRows.push([label, value]);
              });
              const summaryWs = XLSX.utils.aoa_to_sheet(summaryRows);
              XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
            }

            const tables = el.querySelectorAll('table');
            if (tables.length === 0 && kpiCards.length === 0) return;
            tables.forEach((t, i) => {
              const ws = XLSX.utils.table_to_sheet(t as HTMLTableElement);
              XLSX.utils.book_append_sheet(wb, ws, 'Table' + (i + 1));
            });
            XLSX.writeFile(wb, 'report-' + tab + '-' + new Date().toISOString().slice(0,10) + '.xlsx');
          }}><Download size={13} /> Excel</button>
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        {groups.map(group => (
          <div key={group} style={{ marginBottom:4 }}>
            <span style={{ fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', letterSpacing:.5, marginRight:8 }}>{group}</span>
            {tabs.filter(t => t.group===group).map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{ padding:'5px 12px', fontSize:11, fontWeight:600, border:'1.5px solid', borderColor: tab===t.key ? 'var(--blue)' : 'var(--bdr)', background: tab===t.key ? 'var(--blue)' : 'transparent', color: tab===t.key ? 'white' : 'var(--tx2)', borderRadius:6, cursor:'pointer', marginRight:4, marginBottom:4 }}>
                {t.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Extra filters */}
      {(tab==='aa') && (
        <div className="filter-bar" style={{ marginBottom:12 }}>
          <select className="form-control" value={accountId} onChange={e => setAccountId(e.target.value)} style={{ width:250 }}>
            <option value="">All Accounts</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
          </select>
        </div>
      )}
      {(tab==='ia' || tab==='sbid') && (
        <div className="filter-bar" style={{ marginBottom:12 }}>
          <div style={{ width:320, display:'flex', gap:6, alignItems:'center' }}>
            <div style={{ flex:1 }}>
              <ProductPicker
                value={productId}
                initialLabel={productName}
                fontSize={12}
                onSelect={(p:any) => { setProductId(String(p.id)); setProductName(p.name); }}
              />
            </div>
            {productId && <button className="btn btn-secondary btn-sm" onClick={() => { setProductId(''); setProductName(''); }}>All</button>}
          </div>
          {tab==='ia' && (
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer', marginLeft:12 }}>
              <input type="checkbox" checked={showCorrections} onChange={e => setShowCorrections(e.target.checked)} />
              Show corrections (edit history)
            </label>
          )}
        </div>
      )}
      {(tab==='cdetail'||tab==='sdetail'||tab==='sbcd') && (
        <div className="filter-bar" style={{ marginBottom:12 }}>
          <div style={{ width:280 }}>
            <ContactPicker value={contactId} initialLabel={contactName}
              placeholder={(tab==='cdetail'?'All customers':'All suppliers') + ' — type to search'}
              onSelect={(ct:any) => { setContactId(String(ct.id)); setContactName(ct.name); }} />
          </div>
          {contactId && <button className="btn btn-secondary btn-sm" onClick={() => { setContactId(''); setContactName(''); }}>Clear</button>}
        </div>
      )}
      {(tab==='iloc') && (
        <div className="filter-bar" style={{ marginBottom:12 }}>
          <select className="form-control" value={locationId} onChange={e => setLocationId(e.target.value)} style={{ width:250 }}>
            <option value="">All Locations</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
      )}

      <div id="report-content">
        <style>{`
          /* ===== QuickBooks-style report skin (applies to every report) ===== */
          #report-content{background:#ffffff;color:#1a1d21;}
          #report-content .card{box-shadow:none;background:#fff;border:1px solid #d9dde3;border-radius:6px;}
          #report-content .card-header{background:#f4f5f7;border-bottom:1px solid #d9dde3;font-weight:700;font-size:12px;letter-spacing:.3px;color:#2b303a;}
          #report-content table{width:100%;border-collapse:collapse;}
          #report-content thead th{background:#f1f3f6;color:#3a4150;text-transform:uppercase;font-size:10px;letter-spacing:.4px;border-top:1px solid #d3d8e0;border-bottom:1.5px solid #aeb6c2;padding:7px 10px;font-weight:700;text-align:left;}
          #report-content thead th[style*="right"]{text-align:right;}
          #report-content tbody td{font-variant-numeric:tabular-nums;padding:5px 10px;border-bottom:1px solid #eef0f3;font-size:12.5px;vertical-align:top;}
          #report-content tbody tr:hover td{background:#fafbfd;}
          #report-content .td-num{text-align:right;white-space:nowrap;}
          #report-content .td-code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;color:#2d6cdf;}
          /* clickable drill-down links (transaction no. / customer name) */
          #report-content .qb-link{color:#2d6cdf;cursor:pointer;}
          #report-content .qb-link:hover{text-decoration:underline;}
          /* QB grouped-report building blocks */
          #report-content tr.qb-group > td{background:#e9edf2;font-weight:700;border-top:1px solid #c7cdd6;border-bottom:1px solid #c7cdd6;color:#1f242c;}
          #report-content tr.qb-sub > td{background:#eaf1fb;font-weight:700;border-top:1px solid #c3d6f2;border-bottom:1px solid #c3d6f2;}
          #report-content tr.qb-grand > td{font-weight:800;border-top:2px solid #333;border-bottom:3px double #333;background:#fff;}
          /* retrofit existing total/opening/closing rows (they use --sur2) into clean QB total rows */
          #report-content tbody tr[style*="var(--sur2)"] > td{background:#eef1f5 !important;font-weight:700;border-top:1px solid #c7cdd6;}
          /* KPI / summary cards */
          #report-content .kpi-card,#report-content .summary-card{background:#fff;border:1px solid #d9dde3;border-radius:6px;}
          /* print: strip the app chrome, keep the report clean on paper */
          @media print{
            .page-header,.filter-bar{display:none !important;}
            #report-content button{display:none !important;}
            #report-content{font-size:11px;}
            #report-content tbody tr{page-break-inside:avoid;}
            #report-content .card{border:none;}
            #report-content thead{display:table-header-group;}
          }
        `}</style>
        <DocumentHeader
          branchName={branchId ? (branches.find((b:any) => String(b.id) === String(branchId))?.name || '') : 'All Branches'}
          branchAddress={branchId ? branches.find((b:any) => String(b.id) === String(branchId))?.address : undefined}
          branchPhone={branchId ? branches.find((b:any) => String(b.id) === String(branchId))?.phone : undefined}
          branchEmail={branchId ? branches.find((b:any) => String(b.id) === String(branchId))?.email : undefined}
          documentTitle={(tabs.find((t:any) => t.key === tab)?.label || 'Report') + (['bs','iv','iloc','ip','ar','ap','csummary','ssummary'].includes(tab) ? '   (As of ' + to + ')' : '   (' + from + ' to ' + to + ')')}
        />
      {loading && <div className="empty-state"><p>Generating report...</p></div>}

      
      {/* TRIAL BALANCE */}
      {!loading && tab==='tb' && data && (
        <>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Total Debits</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(sum.total_debit)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Credits</div><div className="kpi-value" style={{fontSize:18,color:'var(--red)'}}>{fmt(sum.total_credit)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Balanced</div><div className="kpi-value" style={{fontSize:18,color:sum.balanced?'var(--green)':'var(--red)'}}>{sum.balanced?'YES':'NO'}</div></div>
          </div>
          <div className="table-wrap"><table>
            <thead><tr><th>Code</th><th>Account</th><th>Type</th><th>Subtype</th><th style={{textAlign:'right'}}>Debit</th><th style={{textAlign:'right'}}>Credit</th><th style={{textAlign:'right'}}>Balance</th></tr></thead>
            <tbody>
              {(Array.isArray(data.data)?data.data:[]).length===0?<tr><td colSpan={7}><div className="empty-state"><p>No data for selected period.</p></div></td></tr>:
              (Array.isArray(data.data)?data.data:[]).map((r:any,i:number) => (
                <tr key={i}>
                  <td className="td-code">{r.account_code}</td>
                  <td style={{fontWeight:600}}>{r.account_name}</td>
                  <td><span className={typeColor[r.type_name]?'badge '+typeColor[r.type_name]:'badge'}>{r.type_name}</span></td>
                  <td style={{fontSize:11,color:'var(--tx3)'}}>{r.subtype_name}</td>
                  <td className="td-num">{Number(r.total_debit)>0?fmt(r.total_debit):'-'}</td>
                  <td className="td-num">{Number(r.total_credit)>0?fmt(r.total_credit):'-'}</td>
                  <td className="td-num" style={{fontWeight:600,color:Number(r.balance)>=0?'var(--blue)':'var(--red)'}}>{fmt(r.balance)}</td>
                </tr>
              ))}
              <tr style={{borderTop:'2px solid var(--bdr)',fontWeight:700,background:'var(--sur2)'}}>
                <td colSpan={4} style={{padding:'10px 14px'}}>TOTAL</td>
                <td className="td-num" style={{color:'var(--blue)'}}>{fmt(sum.total_debit)}</td>
                <td className="td-num" style={{color:'var(--red)'}}>{fmt(sum.total_credit)}</td>
                <td></td>
              </tr>
            </tbody>
          </table></div>
        </>
      )}

      {/* Balance Sheet */}
      {!loading && tab==='bs' && data && (() => {
        const d = data.data || {};
        const sum = data.summary || {};
        const money = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
        const ordered = (obj:any, pref:string[]) => { const ks = Object.keys(obj||{}); return [...pref.filter(k=>ks.includes(k)), ...ks.filter(k=>!pref.includes(k))]; };
        const sub = (items:any[]) => (items||[]).reduce((t:number,r:any)=>t+Number(r.balance||0),0);
        const amtR: React.CSSProperties = { textAlign:'right', whiteSpace:'nowrap', padding:'2px 14px' };
        const acct = (r:any,i:number,pad:number) => (<tr key={i}><td></td><td></td><td style={{padding:'2px 8px 2px '+pad+'px'}}>{r.account_name}</td><td style={amtR}>{money(r.balance)}</td></tr>);
        const block = (label:string, items:any[]) => (items&&items.length) ? (
          <React.Fragment key={'b'+label}>
            <tr><td></td><td></td><td style={{fontWeight:600,padding:'4px 8px 2px 24px'}}>{label}</td><td></td></tr>
            {items.map((r:any,i:number)=>acct(r,i,44))}
            <tr><td></td><td></td><td style={{padding:'3px 8px 3px 24px',borderTop:'1px solid var(--bdr)',fontStyle:'italic'}}>Total {label}</td><td style={{...amtR,borderTop:'1px solid var(--bdr)'}}>{money(sub(items))}</td></tr>
          </React.Fragment>
        ) : null;
        const l1group = (label:string, items:any[]) => (items&&items.length) ? (
          <React.Fragment key={'g'+label}>
            <tr><td></td><td colSpan={2} style={{fontWeight:700,padding:'8px 8px 2px'}}>{label}</td><td></td></tr>
            {items.map((r:any,i:number)=>acct(r,i,36))}
            <tr><td></td><td colSpan={2} style={{padding:'3px 8px 3px 16px',borderTop:'1px solid var(--tx2)',fontWeight:700}}>Total {label}</td><td style={{...amtR,borderTop:'1px solid var(--tx2)',fontWeight:700}}>{money(sub(items))}</td></tr>
          </React.Fragment>
        ) : null;
        const grp = (typeKey:string, pref:string[]) => ordered(d[typeKey], pref).map((st:string) => (
          <React.Fragment key={typeKey+st}>
            <tr><td></td><td></td><td style={{fontWeight:600,padding:'4px 8px 3px 20px',color:'var(--tx2)'}}>{st}</td><td></td></tr>
            {(d[typeKey][st]||[]).map((r:any,i:number)=>acct(r,i,36))}
            <tr><td></td><td></td><td style={{padding:'3px 8px 3px 20px',borderTop:'1px solid var(--bdr)',fontStyle:'italic'}}>Total {st}</td><td style={{...amtR,borderTop:'1px solid var(--bdr)'}}>{money(sub(d[typeKey][st]))}</td></tr>
          </React.Fragment>
        ));
        const ca = d.asset?.['Current Asset'] || [];
        const pre = (r:any) => String(r.account_code||'');
        const cash = ca.filter((r:any)=>pre(r).startsWith('10'));
        const ar   = ca.filter((r:any)=>pre(r).startsWith('11'));
        const inv  = ca.filter((r:any)=>pre(r).startsWith('12'));
        const otherCA = ca.filter((r:any)=>!(pre(r).startsWith('10')||pre(r).startsWith('11')||pre(r).startsWith('12')));
        const tle = (Number(sum.total_liabilities)||0) + (Number(sum.total_equity)||0);
        return (
          <div style={{maxWidth:760,margin:'0 auto'}}>
            <div style={{textAlign:'center',marginBottom:16}}>
              <div style={{fontSize:17,fontWeight:700}}>Balance Sheet</div>
              <div style={{fontSize:12,color:'var(--tx3)'}}>As of {sum.as_of}</div>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <colgroup><col style={{width:'24px'}}/><col style={{width:'24px'}}/><col/><col style={{width:'150px'}}/></colgroup>
              <tbody>
                <tr><td colSpan={3} style={{fontWeight:700,padding:'8px 8px 4px',textTransform:'uppercase'}}>Assets</td><td></td></tr>
                <tr><td></td><td colSpan={2} style={{fontWeight:700,padding:'6px 8px 2px'}}>Current Assets</td><td></td></tr>
                {block('Cash & Bank', cash)}
                {block('Accounts Receivable', ar)}
                {block('Inventory', inv)}
                {block('Other Current Assets', otherCA)}
                <tr><td></td><td colSpan={2} style={{fontWeight:700,padding:'4px 8px',borderTop:'1px solid var(--tx2)'}}>Total Current Assets</td><td style={{...amtR,fontWeight:700,borderTop:'1px solid var(--tx2)'}}>{money(sub(ca))}</td></tr>
                {l1group('Fixed Assets', d.asset?.['Fixed Asset']||[])}
                {l1group('Other Assets', d.asset?.['Other Asset']||[])}
                <tr><td colSpan={3} style={{fontWeight:700,padding:'7px 8px',borderTop:'2px solid var(--tx2)'}}>TOTAL ASSETS</td><td style={{...amtR,fontWeight:700,borderTop:'2px solid var(--tx2)'}}>{money(sum.total_assets)}</td></tr>
                <tr><td colSpan={4} style={{height:16}}></td></tr>
                <tr><td colSpan={3} style={{fontWeight:700,padding:'8px 8px 4px',textTransform:'uppercase'}}>Liabilities &amp; Equity</td><td></td></tr>
                <tr><td></td><td colSpan={2} style={{fontWeight:700,padding:'4px 8px'}}>Liabilities</td><td></td></tr>
                {grp('liability',['Current Liability','Long Term Liability'])}
                <tr><td></td><td colSpan={2} style={{fontWeight:700,padding:'4px 8px 4px 16px',borderTop:'1px solid var(--tx2)'}}>Total Liabilities</td><td style={{...amtR,fontWeight:700,borderTop:'1px solid var(--tx2)'}}>{money(sum.total_liabilities)}</td></tr>
                <tr><td></td><td colSpan={2} style={{fontWeight:700,padding:'12px 8px 4px'}}>Equity</td><td></td></tr>
                {grp('equity',['Owner Capital','Retained Earnings','Drawings'])}
                <tr><td></td><td></td><td style={{padding:'2px 8px 2px 36px'}}>Retained Earnings (Net Income)</td><td style={amtR}>{money(sum.retained_earnings)}</td></tr>
                <tr><td></td><td colSpan={2} style={{fontWeight:700,padding:'4px 8px 4px 16px',borderTop:'1px solid var(--tx2)'}}>Total Equity</td><td style={{...amtR,fontWeight:700,borderTop:'1px solid var(--tx2)'}}>{money(sum.total_equity)}</td></tr>
                <tr><td colSpan={3} style={{fontWeight:700,padding:'7px 8px',borderTop:'2px solid var(--tx2)'}}>TOTAL LIABILITIES &amp; EQUITY</td><td style={{...amtR,fontWeight:700,borderTop:'2px solid var(--tx2)'}}>{money(tle)}</td></tr>
              </tbody>
            </table>
            {!sum.balanced && (
              <div style={{marginTop:12,padding:'8px 12px',background:'rgba(255,60,60,0.08)',border:'1px solid var(--red)',borderRadius:6,fontSize:12,color:'var(--red)'}}>
                Out of balance by {money((Number(sum.total_assets)||0) - tle)}.
              </div>
            )}
          </div>
        );
      })()}

      {!loading && tab==='pl' && data && (() => {
        const d = data.data || {};
        const sum = data.summary || {};
        const money = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
        const rev = d.revenue || [];
        const cogs = d.cogs || [];
        const exp = d.expenses || [];
        const opEx = exp.filter((r:any)=>r.subtype_code!=='other_expense');
        const otherEx = exp.filter((r:any)=>r.subtype_code==='other_expense');
        const sumAmt = (arr:any[]) => arr.reduce((t:number,r:any)=>t+Number(r.amount||0),0);
        const totOpEx = sumAmt(opEx);
        const totOtherEx = sumAmt(otherEx);
        const netOrdinary = Number(sum.gross_profit||0) - totOpEx;
        const netOther = -totOtherEx;
        const hasOther = otherEx.length > 0;
        const amtR: React.CSSProperties = { textAlign:'right', whiteSpace:'nowrap', padding:'2px 14px' };
        const lines = (arr:any[]) => arr.map((r:any,i:number)=>(<tr key={i}><td></td><td></td><td style={{padding:'2px 8px 2px 36px'}}>{r.account_name}</td><td style={amtR}>{money(r.amount)}</td></tr>));
        const subhead = (label:string) => (<tr><td></td><td colSpan={2} style={{fontWeight:600,padding:'6px 8px 2px 20px'}}>{label}</td><td></td></tr>);
        const totalRow = (label:string, val:any) => (<tr><td></td><td colSpan={2} style={{fontStyle:'italic',padding:'3px 8px 3px 20px',borderTop:'1px solid var(--bdr)'}}>{label}</td><td style={{...amtR,borderTop:'1px solid var(--bdr)'}}>{money(val)}</td></tr>);
        return (
          <div style={{maxWidth:760,margin:'0 auto'}}>
            <div style={{textAlign:'center',marginBottom:16}}>
              <div style={{fontSize:17,fontWeight:700}}>Profit &amp; Loss</div>
              <div style={{fontSize:12,color:'var(--tx3)'}}>{sum.from} to {sum.to}</div>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <colgroup><col style={{width:'24px'}}/><col style={{width:'24px'}}/><col/><col style={{width:'150px'}}/></colgroup>
              <tbody>
                <tr><td colSpan={3} style={{fontWeight:700,padding:'8px 8px 4px',textTransform:'uppercase'}}>Ordinary Income / Expense</td><td></td></tr>
                {subhead('Income')}
                {lines(rev)}
                {totalRow('Total Income', sum.total_revenue)}
                {cogs.length>0 && subhead('Cost of Goods Sold')}
                {lines(cogs)}
                {cogs.length>0 && totalRow('Total COGS', sum.total_cogs)}
                <tr><td></td><td colSpan={2} style={{fontWeight:700,padding:'6px 8px 6px 16px',borderTop:'1px solid var(--tx2)'}}>Gross Profit</td><td style={{...amtR,fontWeight:700,borderTop:'1px solid var(--tx2)'}}>{money(sum.gross_profit)}</td></tr>
                {subhead('Expense')}
                {lines(opEx)}
                {totalRow('Total Expense', totOpEx)}
                <tr><td colSpan={3} style={{fontWeight:700,padding:'6px 8px',borderTop:'2px solid var(--tx2)'}}>Net Ordinary Income</td><td style={{...amtR,fontWeight:700,borderTop:'2px solid var(--tx2)'}}>{money(netOrdinary)}</td></tr>
                {hasOther && <tr><td colSpan={4} style={{height:12}}></td></tr>}
                {hasOther && <tr><td colSpan={3} style={{fontWeight:700,padding:'8px 8px 4px',textTransform:'uppercase'}}>Other Income / Expense</td><td></td></tr>}
                {hasOther && subhead('Other Expense')}
                {hasOther && lines(otherEx)}
                {hasOther && totalRow('Total Other Expense', totOtherEx)}
                {hasOther && <tr><td></td><td colSpan={2} style={{fontWeight:700,padding:'4px 8px 4px 16px',borderTop:'1px solid var(--tx2)'}}>Net Other Income</td><td style={{...amtR,fontWeight:700,borderTop:'1px solid var(--tx2)'}}>{money(netOther)}</td></tr>}
                <tr><td colSpan={3} style={{fontWeight:700,fontSize:14,padding:'8px',borderTop:'3px double var(--tx2)'}}>Net Income</td><td style={{...amtR,fontWeight:700,fontSize:14,borderTop:'3px double var(--tx2)'}}>{money(sum.net_profit)}</td></tr>
              </tbody>
            </table>
          </div>
        );
      })()}

      {!loading && tab==='aa' && data && (
        <>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Opening Balance</div><div className="kpi-value" style={{fontSize:18}}>{fmt(sum.opening_balance)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Debits</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(sum.total_debit)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Credits</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(sum.total_credit)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Closing Balance</div><div className="kpi-value" style={{fontSize:18,fontWeight:700}}>{fmt(sum.closing_balance)}</div></div>
          </div>
          <div className="table-wrap"><table>
            <thead><tr><th>#</th><th>Date</th><th>Account</th><th>Type</th><th>Contact</th><th>Note</th><th style={{textAlign:'right'}}>Debit</th><th style={{textAlign:'right'}}>Credit</th><th style={{textAlign:'right'}}>Balance</th></tr></thead>
            <tbody>
              <tr style={{background:'var(--sur2)',fontWeight:600}}><td colSpan={8} style={{padding:'8px 14px'}}>Opening Balance</td><td className="td-num" style={{fontWeight:700}}>{fmt(sum.opening_balance)}</td></tr>
              {tb.length===0?<tr><td colSpan={9}><div className="empty-state"><p>No activity.</p></div></td></tr>:tb.map((r:any,i:number) => <tr key={i}><td className="td-code">#{r.journal_id}</td><td style={{fontSize:11,color:'var(--tx3)'}}>{new Date(r.entry_date).toLocaleDateString()}</td><td style={{fontSize:11}}>{r.account_code} {r.account_name}</td><td style={{fontSize:11}}><span className="badge badge-blue">{r.type}</span></td><td style={{fontSize:11,color:'var(--tx3)'}}>{r.contact_name||r.entry_contact||'-'}</td><td style={{fontSize:11,color:'var(--tx3)'}}>{(() => { const d = r.journal_desc || r.line_desc || ''; const i = d.indexOf(' \u2014 '); return i >= 0 ? d.slice(i + 3) : (d || '-'); })()}</td><td className="td-num">{Number(r.debit)>0?fmt(r.debit):'-'}</td><td className="td-num">{Number(r.credit)>0?fmt(r.credit):'-'}</td><td className="td-num" style={{fontWeight:600}}>{fmt(r.balance)}</td></tr>)}
              <tr style={{borderTop:'2px solid var(--bdr)',fontWeight:700,background:'var(--sur2)'}}><td colSpan={8} style={{padding:'10px 14px'}}>Closing Balance</td><td className="td-num" style={{fontWeight:700}}>{fmt(sum.closing_balance)}</td></tr>
            </tbody>
          </table></div>
        </>
      )}

      {!loading && tab==='uact' && data && (() => {
        const d:any = data.data || {};
        const us:any[] = d.users || [];
        const docs:any[] = (d.documents || []).filter((x:any) => !uaUser || x.user_id === uaUser);
        return (
          <>
            <div className="summary-row">
              <div className="summary-card"><div className="summary-label">Sales</div><div className="summary-value">{fmt(d.total_sales)}</div></div>
              <div className="summary-card"><div className="summary-label">Receipts</div><div className="summary-value">{fmt(d.total_receipts)}</div></div>
              <div className="summary-card"><div className="summary-label">Corrections</div><div className="summary-value" style={{color: d.total_edits ? 'var(--amber)' : undefined}}>{d.total_edits || 0}</div></div>
              <div className="summary-card"><div className="summary-label">Voids</div><div className="summary-value" style={{color: d.total_voids ? 'var(--red)' : undefined}}>{d.total_voids || 0}</div></div>
            </div>
            <div className="table-wrap" style={{marginBottom:16}}><table>
              <thead><tr>
                <th>User</th><th style={{textAlign:'right'}}>Sales</th><th style={{textAlign:'right'}}>Value</th>
                <th style={{textAlign:'right'}}>Receipts</th><th style={{textAlign:'right'}}>Value</th>
                <th style={{textAlign:'right'}}>Payments</th><th style={{textAlign:'right'}}>GRNs</th>
                <th style={{textAlign:'right'}}>Tickets</th><th style={{textAlign:'right'}}>Edits</th><th style={{textAlign:'right'}}>Voids</th>
              </tr></thead>
              <tbody>
                {us.length===0 ? <tr><td colSpan={10}><div className="empty-state"><p>Nothing recorded in this period.</p></div></td></tr>
                : us.map((u:any) => (
                  <tr key={u.user_id} onClick={() => setUaUser(uaUser === u.user_id ? null : u.user_id)}
                      style={{cursor:'pointer', background: uaUser === u.user_id ? 'var(--sur2)' : undefined}}>
                    <td style={{fontWeight:600,fontSize:12}}>{u.user}</td>
                    <td className="td-num">{u.sales || '-'}</td>
                    <td className="td-num">{u.sales_value ? fmt(u.sales_value) : '-'}</td>
                    <td className="td-num">{u.receipts || '-'}</td>
                    <td className="td-num">{u.receipts_value ? fmt(u.receipts_value) : '-'}</td>
                    <td className="td-num">{u.payments || '-'}</td>
                    <td className="td-num">{u.grns || '-'}</td>
                    <td className="td-num">{u.tickets || '-'}</td>
                    <td className="td-num" style={{color: u.edits ? 'var(--amber)' : undefined, fontWeight: u.edits ? 600 : 400}}>{u.edits || '-'}</td>
                    <td className="td-num" style={{color: u.voids ? 'var(--red)' : undefined, fontWeight: u.voids ? 600 : 400}}>{u.voids || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
              <span style={{fontSize:12,fontWeight:600}}>Documents {uaUser ? '— ' + (us.find((u:any)=>u.user_id===uaUser)?.user || '') : ''}</span>
              {uaUser && <button className="btn btn-secondary btn-sm" onClick={() => setUaUser(null)}>Show everyone</button>}
              <span className="record-count" style={{marginLeft:'auto'}}>{docs.length}</span>
            </div>
            <div className="table-wrap"><table>
              <thead><tr><th>Date</th><th>User</th><th>Kind</th><th>Reference</th><th style={{textAlign:'right'}}>Amount</th><th>Entered</th><th>Note</th></tr></thead>
              <tbody>
                {docs.length===0 ? <tr><td colSpan={7}><div className="empty-state"><p>No documents.</p></div></td></tr>
                : docs.map((t:any,i:number) => (
                  <tr key={i}>
                    <td style={{fontSize:11}}>{t.date || '-'}</td>
                    <td style={{fontSize:11}}>{t.user}</td>
                    <td style={{fontSize:11,color: t.kind==='Void' ? 'var(--red)' : t.kind==='Correction' ? 'var(--amber)' : undefined}}>{t.kind}</td>
                    <td className="td-code">{t.ref}</td>
                    <td className="td-num">{t.amount ? fmt(t.amount) : '-'}</td>
                    <td style={{fontSize:10,color:'var(--tx3)'}}>{String(t.at||'').slice(0,16).replace('T',' ')}</td>
                    <td style={{fontSize:11,color:'var(--tx3)'}}>{t.note || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </>
        );
      })()}

      {!loading && tab==='tkt' && data && (() => {
        const d:any = data.data || {};
        const ts:any[] = d.tickets || [];
        return (
          <>
            <div className="summary-row">
              <div className="summary-card"><div className="summary-label">In The Shop</div><div className="summary-value">{d.total || 0}</div></div>
              <div className="summary-card"><div className="summary-label">Past Promised Date</div><div className="summary-value" style={{color: d.overdue ? 'var(--red)' : undefined}}>{d.overdue || 0}</div></div>
              <div className="summary-card"><div className="summary-label">Held Over 7 Days</div><div className="summary-value" style={{color: d.over_7_days ? 'var(--red)' : undefined}}>{d.over_7_days || 0}</div></div>
              <div className="summary-card"><div className="summary-label">Value Held</div><div className="summary-value">{fmt(d.value_held)}</div></div>
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',margin:'0 0 12px'}}>
              {Object.entries(d.by_stage || {}).map(([s,n]:any) => (
                <span key={s} className="badge badge-blue" style={{fontSize:11}}>{s}: {n}</span>
              ))}
            </div>
            <div className="table-wrap"><table>
              <thead><tr>
                <th>Ticket</th><th>Received</th><th>Customer</th><th>Device</th><th>Problem</th>
                <th>Stage</th><th style={{textAlign:'right'}}>Days</th><th>Promised</th><th>Technician</th><th style={{textAlign:'right'}}>Value</th>
              </tr></thead>
              <tbody>
                {ts.length===0 ? <tr><td colSpan={10}><div className="empty-state"><p>Nothing in the shop.</p></div></td></tr>
                : ts.map((t:any) => (
                  <tr key={t.id}>
                    <td className="td-code">{t.ticket_no}</td>
                    <td style={{fontSize:11}}>{t.received_date || '-'}</td>
                    <td style={{fontSize:11}}>{t.customer || '-'}</td>
                    <td style={{fontSize:11}}>{[t.device_brand,t.device_model].filter(Boolean).join(' ') || '-'}</td>
                    <td style={{fontSize:11,color:'var(--tx3)'}}>{String(t.problem_description||'').slice(0,38)}</td>
                    <td style={{fontSize:11}}>{t.on_hold ? <span style={{color:'var(--red)'}}>On hold{t.on_hold_reason ? ' — '+t.on_hold_reason : ''}</span> : t.stage}</td>
                    <td className="td-num" style={{fontWeight:600,color: t.days_held > 7 ? 'var(--red)' : undefined}}>{t.days_held}</td>
                    <td style={{fontSize:11,color: t.overdue ? 'var(--red)' : 'var(--tx3)'}}>{t.promised_date || '-'}</td>
                    <td style={{fontSize:11}}>{t.technician || <span style={{color:'var(--red)'}}>unassigned</span>}</td>
                    <td className="td-num">{fmt(t.total_amount || t.estimated_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </>
        );
      })()}

      {/* AR/AP Aging */}
      {!loading && (tab==='ar'||tab==='ap') && data && (
        <div className="table-wrap"><table>
          <thead><tr><th>{tab==='ar'?'Customer':'Supplier'}</th><th style={{textAlign:'right'}}>Balance</th><th style={{textAlign:'right'}}>Current</th><th style={{textAlign:'right'}}>1-30</th><th style={{textAlign:'right'}}>31-60</th><th style={{textAlign:'right'}}>61-90</th><th style={{textAlign:'right'}}>Over 90</th></tr></thead>
          <tbody>{(Array.isArray(data.data)?data.data:[]).length===0?<tr><td colSpan={7}><div className="empty-state"><p>No data.</p></div></td></tr>:(Array.isArray(data.data)?data.data:[]).map((r:any,i:number) => <tr key={i}><td><div style={{fontWeight:600}}>{r.name}</div><div style={{fontSize:11,color:'var(--tx3)'}}>{r.mobile}</div></td><td className="td-num" style={{fontWeight:700,color:tab==='ar'?'var(--blue)':'var(--red)'}}>{fmt(r[tab==='ar'?'ar_balance':'ap_balance'])}</td><td className="td-num">{fmt(r.current)}</td><td className="td-num" style={{color:'var(--amber)'}}>{fmt(r['1_30'])}</td><td className="td-num" style={{color:'var(--amber)'}}>{fmt(r['31_60'])}</td><td className="td-num" style={{color:'var(--red)'}}>{fmt(r['61_90'])}</td><td className="td-num" style={{color:'var(--red)',fontWeight:700}}>{fmt(r.over_90)}</td></tr>)}</tbody>
        </table></div>
      )}

      {/* Customer Balance Summary */}
      {!loading && tab==='csummary' && data && (
        <>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Total AR</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(sum.total_ar)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Customers</div><div className="kpi-value" style={{fontSize:18}}>{sum.count}</div></div>
          </div>
          <label style={{fontSize:12,display:'flex',alignItems:'center',gap:6,marginBottom:8}}><input type="checkbox" checked={owingOnly} onChange={e => setOwingOnly(e.target.checked)} /> Only those with a balance</label>
          <div className="table-wrap"><table>
            <thead><tr><th>Customer</th><th>Mobile</th><th>Email</th><th style={{textAlign:'right'}}>AR Balance</th><th style={{textAlign:'right'}}>Credit Limit</th><th style={{textAlign:'right'}}>Payment Terms</th></tr></thead>
            <tbody>{(Array.isArray(data.data)?data.data:[]).filter((r:any) => !owingOnly || Math.abs(Number(r.ar_balance ?? r.ap_balance ?? 0)) > 0.009).map((r:any,i:number) => <tr key={i}><td style={{fontWeight:600}}>{r.name}</td><td style={{color:'var(--tx3)'}}>{r.mobile||'-'}</td><td style={{color:'var(--tx3)',fontSize:11}}>{r.email||'-'}</td><td className="td-num" style={{color:'var(--blue)',fontWeight:700}}>{fmt(r.ar_balance)}</td><td className="td-num">{fmt(r.credit_limit)}</td><td style={{textAlign:'right',fontSize:11,color:'var(--tx3)'}}>{r.payment_terms_days} days</td></tr>)}</tbody>
          </table></div>
        </>
      )}

      {/* Customer Balance Detail */}
      {!loading && tab==='cdetail' && data && (() => {
        const customers = data.data?.customers || [];
        const s2 = data.summary || {};
        const money = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
        const amount = (l:any) => Number(l.debit||0) - Number(l.credit||0);
        const openCust = (c:any) => { setContactId(String(c.contact_id)); setContactName(c.name); setTab('cactivity'); };
        return (
          <div className="card" style={{padding:0}}>
            <table>
              <thead><tr>
                <th>Type</th><th>Date</th><th>Number</th><th>Account</th><th>Branch</th>
                <th style={{textAlign:'right'}}>Amount</th><th style={{textAlign:'right'}}>Balance</th>
              </tr></thead>
              <tbody>
                {customers.length===0 ? <tr><td colSpan={7}><div className="empty-state"><p>No customer activity in this period.</p></div></td></tr>
                : customers.map((c:any) => {
                  const show = !collapsed['c'+c.contact_id];
                  return (
                    <React.Fragment key={c.contact_id}>
                      <tr className="qb-group" style={{cursor:'pointer'}} onClick={() => setCollapsed(st => ({...st, ['c'+c.contact_id]: !st['c'+c.contact_id]}))}>
                        <td colSpan={5}>
                          <span style={{display:'inline-block',width:14,color:'var(--tx3)'}}>{show?'▾':'▸'}</span>
                          <span className="qb-link" onClick={(e) => { e.stopPropagation(); openCust(c); }}>{c.name}</span>
                        </td>
                        <td className="td-num"></td>
                        <td className="td-num">{money(c.closing_balance)}</td>
                      </tr>
                      {show && (<>
                        <tr><td style={{fontStyle:'italic',color:'var(--tx3)'}}>Opening Balance</td><td></td><td></td><td style={{fontSize:11,color:'var(--tx3)'}}>Accounts Receivable</td><td></td><td className="td-num"></td><td className="td-num">{money(c.opening_balance)}</td></tr>
                        {c.transactions.map((l:any,i:number) => (
                          <tr key={i}>
                            <td style={{fontSize:11}}>{l.type}</td>
                            <td style={{fontSize:11}}>{l.date?new Date(l.date).toLocaleDateString():''}</td>
                            <td className="td-code">{l.reference}</td>
                            <td style={{fontSize:11,color:'var(--tx3)'}}>Accounts Receivable</td>
                            <td style={{fontSize:11,color:'var(--tx3)'}}>{l.branch||''}</td>
                            <td className="td-num" style={{color: amount(l)<0?'var(--green)':undefined}}>{money(amount(l))}</td>
                            <td className="td-num">{money(l.balance)}</td>
                          </tr>
                        ))}
                        <tr className="qb-sub"><td colSpan={5}>Subtotal — {c.name}</td><td className="td-num">{money(c.period_net)}</td><td className="td-num">{money(c.closing_balance)}</td></tr>
                      </>)}
                    </React.Fragment>
                  );
                })}
                {customers.length>0 && (
                  <tr className="qb-grand"><td colSpan={5}>GRAND TOTAL</td><td className="td-num">{money(s2.grand_net)}</td><td className="td-num">{money(s2.grand_closing)}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Supplier Balance Summary */}
      {!loading && tab==='ssummary' && data && (
        <>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Total AP</div><div className="kpi-value" style={{fontSize:18,color:'var(--red)'}}>{fmt(sum.total_ap)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Suppliers</div><div className="kpi-value" style={{fontSize:18}}>{sum.count}</div></div>
          </div>
          <label style={{fontSize:12,display:'flex',alignItems:'center',gap:6,marginBottom:8}}><input type="checkbox" checked={owingOnly} onChange={e => setOwingOnly(e.target.checked)} /> Only those with a balance</label>
          <div className="table-wrap"><table>
            <thead><tr><th>Supplier</th><th>Mobile</th><th style={{textAlign:'right'}}>AP Balance</th><th style={{textAlign:'right'}}>Payment Terms</th></tr></thead>
            <tbody>{(Array.isArray(data.data)?data.data:[]).filter((r:any) => !owingOnly || Math.abs(Number(r.ar_balance ?? r.ap_balance ?? 0)) > 0.009).map((r:any,i:number) => <tr key={i}><td style={{fontWeight:600}}>{r.name}</td><td style={{color:'var(--tx3)'}}>{r.mobile||'-'}</td><td className="td-num" style={{color:'var(--red)',fontWeight:700}}>{fmt(r.ap_balance)}</td><td style={{textAlign:'right',fontSize:11,color:'var(--tx3)'}}>{r.payment_terms_days} days</td></tr>)}</tbody>
          </table></div>
        </>
      )}

      {/* Supplier Balance Detail */}
      {!loading && tab==='sdetail' && data && (
        <>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Total Invoiced</div><div className="kpi-value" style={{fontSize:18,color:'var(--red)'}}>{fmt(sum.total_invoiced)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Paid</div><div className="kpi-value" style={{fontSize:18,color:'var(--green)'}}>{fmt(sum.total_paid)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Balance</div><div className="kpi-value" style={{fontSize:18,color:(sum.balance||0)>0?'var(--red)':'var(--green)'}}>{fmt(sum.balance)}</div></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="card"><div className="card-header"><span>Invoices</span></div><table>
              <thead><tr><th>Invoice</th><th>Supplier</th><th style={{textAlign:'right'}}>Amount</th><th>Due</th></tr></thead>
              <tbody>{(data.data?.invoices||[]).map((r:any,i:number) => <tr key={i}><td className="td-code">{r.invoice_no}</td><td style={{fontSize:11}}>{r.supplier_name}</td><td className="td-num td-red">{fmt(r.invoice_amount)}</td><td style={{fontSize:11,color:'var(--tx3)'}}>{r.due_date?new Date(r.due_date).toLocaleDateString():'-'}</td></tr>)}</tbody>
            </table></div>
            <div className="card"><div className="card-header"><span>Payments & Returns</span></div><table>
              <thead><tr><th>Date</th><th>Type</th><th>Description</th><th style={{textAlign:"right"}}>Amount</th></tr></thead>
              <tbody>{(data.data?.payments||[]).map((r:any,i:number) => <tr key={i}><td style={{fontSize:11}}>{r.paid_at}</td><td style={{fontSize:11,color:"var(--tx3)"}}>{r.type}</td><td style={{fontSize:11,color:"var(--tx3)"}}>{r.payment_ref||"-"}</td><td className="td-num td-green">{fmt(r.amount)}</td></tr>)}</tbody>
            </table></div>
          </div>
        </>
      )}

      


      {/* Sales History */}
      {!loading && tab==='sh' && data && (
        <>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Total Sales</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(sum.total_sales)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Profit</div><div className="kpi-value" style={{fontSize:18,color:'var(--green)'}}>{fmt(sum.total_profit)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Count</div><div className="kpi-value" style={{fontSize:18}}>{sum.count}</div></div>
          </div>
          <label style={{fontSize:12,display:'flex',alignItems:'center',gap:6,marginBottom:8}}><input type="checkbox" checked={owingOnly} onChange={e => setOwingOnly(e.target.checked)} /> Only those with a balance</label>
          <div className="table-wrap"><table>
            <thead><tr><th>Sale No</th><th>Customer</th><th>Type</th><th>Status</th><th>Date</th><th style={{textAlign:'right'}}>Amount</th><th style={{textAlign:'right'}}>Profit</th></tr></thead>
            <tbody>
              {(Array.isArray(data.data)?data.data:[]).map((r:any,i:number) => (<tr key={i}><td className="td-code">{r.sale_no}</td><td style={{fontSize:11}}>{r.customer}</td><td style={{fontSize:11}}>{r.sale_type}</td><td style={{fontSize:11}}>{r.status}</td><td style={{fontSize:11}}>{r.sale_date}</td><td className="td-num">{Number(r.total_amount).toFixed(2)}</td><td className="td-num">{Number(r.gross_profit||0).toFixed(2)}</td></tr>))}
              <tr style={{borderTop:'2px solid var(--bdr)',fontWeight:700,background:'var(--sur2)'}}><td colSpan={5} style={{padding:'10px 14px'}}>Total</td><td className="td-num">{fmt(sum.total_sales)}</td><td className="td-num">{fmt(sum.total_profit)}</td></tr>
            </tbody>
          </table></div>
        </>
      )}

      {/* GRN History */}
      {!loading && tab==='gh' && data && (
        <>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Total GRNs</div><div className="kpi-value" style={{fontSize:18}}>{sum.count}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Amount</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(sum.total_amount)}</div></div>
          </div>
          <label style={{fontSize:12,display:'flex',alignItems:'center',gap:6,marginBottom:8}}><input type="checkbox" checked={owingOnly} onChange={e => setOwingOnly(e.target.checked)} /> Only those with a balance</label>
          <div className="table-wrap"><table>
            <thead><tr><th>GRN No</th><th>Supplier</th><th>Branch</th><th>Status</th><th>Date</th><th style={{textAlign:'right'}}>Amount</th><th style={{textAlign:'right'}}>Landed Cost</th></tr></thead>
            <tbody>
              {(Array.isArray(data.data)?data.data:[]).map((r:any,i:number) => (<tr key={i}><td className="td-code">{r.grn_no}</td><td style={{fontSize:11}}>{r.supplier}</td><td style={{fontSize:11}}>{r.branch}</td><td style={{fontSize:11}}>{r.status}</td><td style={{fontSize:11}}>{r.grn_date}</td><td className="td-num">{Number(r.total_amount).toFixed(2)}</td><td className="td-num">{Number(r.landed_cost_total||0).toFixed(2)}</td></tr>))}
              <tr style={{borderTop:'2px solid var(--bdr)',fontWeight:700,background:'var(--sur2)'}}><td colSpan={5} style={{padding:'10px 14px'}}>Total</td><td className="td-num">{fmt(sum.total_amount)}</td><td></td></tr>
            </tbody>
          </table></div>
        </>
      )}
      {/* Customer Activity */}
      {!loading && tab==='cactivity' && data && (
        <>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Customer</div><div className="kpi-value" style={{fontSize:16}}>{data.data?.contact?.name}</div></div>
            <div className="kpi-card"><div className="kpi-label">Opening Balance</div><div className="kpi-value" style={{fontSize:18,color:Number(data.data?.opening_balance||0)>=0?'var(--blue)':'var(--red)'}}>{fmt(data.data?.opening_balance)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Charged</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(data.data?.total_debit)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Paid</div><div className="kpi-value" style={{fontSize:18,color:'var(--green)'}}>{fmt(data.data?.total_credit)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Closing Balance</div><div className="kpi-value" style={{fontSize:18,color:Number(data.data?.closing_balance||0)>=0?'var(--blue)':'var(--red)'}}>{fmt(data.data?.closing_balance)}</div></div>
          </div>
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Type</th><th>Reference</th><th>Description</th><th style={{textAlign:'right'}}>Debit</th><th style={{textAlign:'right'}}>Credit</th><th style={{textAlign:'right'}}>Balance</th></tr></thead>
            <tbody>
              <tr style={{background:'var(--sur2)',fontWeight:600}}><td colSpan={4} style={{padding:'8px 14px'}}>Opening Balance</td><td></td><td></td><td className="td-num" style={{color:Number(data.data?.opening_balance||0)>=0?'var(--blue)':'var(--red)',fontWeight:700}}>{fmt(data.data?.opening_balance)}</td></tr>
              {(data.data?.transactions||[]).map((r:any,i:number) => (
                <tr key={i}>
                  <td style={{fontSize:11}}>{r.date}</td>
                  <td style={{fontSize:11,color:'var(--tx3)'}}>{r.type}</td>
                  <td className="td-code">{r.reference}</td>
                  <td style={{fontSize:11}}>{r.description}</td>
                  <td className="td-num" style={{color:'var(--blue)'}}>{Number(r.debit)>0?fmt(r.debit):'-'}</td>
                  <td className="td-num" style={{color:'var(--green)'}}>{Number(r.credit)>0?fmt(r.credit):'-'}</td>
                  <td className="td-num" style={{fontWeight:600,color:Number(r.balance)>=0?'var(--blue)':'var(--red)'}}>{fmt(r.balance)}</td>
                </tr>
              ))}
              <tr style={{borderTop:'2px solid var(--bdr)',fontWeight:700,background:'var(--sur2)'}}><td colSpan={4} style={{padding:'10px 14px'}}>Closing Balance</td><td className="td-num" style={{color:'var(--blue)'}}>{fmt(data.data?.total_debit)}</td><td className="td-num" style={{color:'var(--green)'}}>{fmt(data.data?.total_credit)}</td><td className="td-num" style={{color:Number(data.data?.closing_balance||0)>=0?'var(--blue)':'var(--red)'}}>{fmt(data.data?.closing_balance)}</td></tr>
            </tbody>
          </table></div>
        </>
      )}

      {/* Supplier Activity */}
      {!loading && tab==='sactivity' && data && (
        <>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Supplier</div><div className="kpi-value" style={{fontSize:16}}>{data.data?.contact?.name}</div></div>
            <div className="kpi-card"><div className="kpi-label">Opening Balance</div><div className="kpi-value" style={{fontSize:18,color:'var(--red)'}}>{fmt(data.data?.opening_balance)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Invoiced</div><div className="kpi-value" style={{fontSize:18,color:'var(--red)'}}>{fmt(data.data?.total_credit)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Paid</div><div className="kpi-value" style={{fontSize:18,color:'var(--green)'}}>{fmt(data.data?.total_debit)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Closing Balance</div><div className="kpi-value" style={{fontSize:18,color:'var(--red)'}}>{fmt(data.data?.closing_balance)}</div></div>
          </div>
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Type</th><th>Reference</th><th>Description</th><th style={{textAlign:'right'}}>Debit (Paid)</th><th style={{textAlign:'right'}}>Credit (Invoiced)</th><th style={{textAlign:'right'}}>Balance</th></tr></thead>
            <tbody>
              <tr style={{background:'var(--sur2)',fontWeight:600}}><td colSpan={4} style={{padding:'8px 14px'}}>Opening Balance</td><td></td><td></td><td className="td-num" style={{color:'var(--red)',fontWeight:700}}>{fmt(data.data?.opening_balance)}</td></tr>
              {(data.data?.transactions||[]).map((r:any,i:number) => (
                <tr key={i}>
                  <td style={{fontSize:11}}>{r.date}</td>
                  <td style={{fontSize:11,color:'var(--tx3)'}}>{r.type}</td>
                  <td className="td-code">{r.reference}</td>
                  <td style={{fontSize:11}}>{r.description}</td>
                  <td className="td-num" style={{color:'var(--green)'}}>{Number(r.debit)>0?fmt(r.debit):'-'}</td>
                  <td className="td-num" style={{color:'var(--red)'}}>{Number(r.credit)>0?fmt(r.credit):'-'}</td>
                  <td className="td-num" style={{fontWeight:600,color:'var(--red)'}}>{fmt(r.balance)}</td>
                </tr>
              ))}
              <tr style={{borderTop:'2px solid var(--bdr)',fontWeight:700,background:'var(--sur2)'}}><td colSpan={4} style={{padding:'10px 14px'}}>Closing Balance</td><td className="td-num" style={{color:'var(--green)'}}>{fmt(data.data?.total_debit)}</td><td className="td-num" style={{color:'var(--red)'}}>{fmt(data.data?.total_credit)}</td><td className="td-num" style={{color:'var(--red)'}}>{fmt(data.data?.closing_balance)}</td></tr>
            </tbody>
          </table></div>
        </>
      )}

      {/* INVENTORY VALUATION PIVOT */}
      {!loading && tab==='iv' && data && (() => {
        const branches = (data.data && data.data.branches) ? data.data.branches : [];
        const grouped = (data.data && data.data.grouped) ? data.data.grouped : {};
        return (
          <>
            <div className="kpi-grid" style={{marginBottom:12}}>
              <div className="kpi-card"><div className="kpi-label">Total Products</div><div className="kpi-value" style={{fontSize:18}}>{sum.total_products}</div></div>
              {canSeeMargin && <div className="kpi-card"><div className="kpi-label">Cost Value</div><div className="kpi-value" style={{fontSize:18}}>{fmt(sum.total_cost_value)}</div></div>}
              <div className="kpi-card"><div className="kpi-label">Retail Value</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(sum.total_retail_value)}</div></div>
              {canSeeMargin && <div className="kpi-card"><div className="kpi-label">Potential Profit</div><div className="kpi-value" style={{fontSize:18,color:(sum.total_potential_profit||0)>=0?'var(--green)':'var(--red)'}}>{fmt(sum.total_potential_profit)}</div></div>}
            </div>
            <div className="table-wrap" style={{overflowX:'auto'}}><table>
              <thead>
                <tr>
                  <th rowSpan={2}>SKU</th><th rowSpan={2}>Product</th><th rowSpan={2}>Category</th>
                  {branches.map((b: string) => <th key={b} colSpan={3} style={{textAlign:'center',borderLeft:'2px solid var(--bdr)',color:'var(--blue)'}}>{b}</th>)}
                  <th colSpan={3} style={{textAlign:'center',borderLeft:'2px solid var(--bdr)',background:'var(--sur2)'}}>Total</th>
                </tr>
                <tr>
                  {branches.map((b: string) => (<React.Fragment key={b}><th style={{textAlign:'right',borderLeft:'2px solid var(--bdr)',fontSize:10,color:'var(--tx3)'}}>Qty</th><th style={{textAlign:'right',fontSize:10,color:'var(--tx3)'}}>Cost Value</th><th style={{textAlign:'right',fontSize:10,color:'var(--tx3)'}}>Retail</th></React.Fragment>))}
                  <th style={{textAlign:'right',borderLeft:'2px solid var(--bdr)',fontSize:10,background:'var(--sur2)'}}>Qty</th>
                  <th style={{textAlign:'right',fontSize:10,background:'var(--sur2)'}}>Cost</th>
                  <th style={{textAlign:'right',fontSize:10,background:'var(--sur2)'}}>Retail</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([category, products], gi) => (
                  <React.Fragment key={gi}>
                    <tr style={{background:'var(--sur2)'}}><td colSpan={3+branches.length*3+3} style={{padding:'8px 14px',fontSize:10,fontWeight:700,color:'var(--tx3)',textTransform:'uppercase',letterSpacing:1}}>{category}</td></tr>
                    {(products as any[]).map((p, i) => (
                      <tr key={i}>
                        <td className="td-code">{p.sku}</td>
                        <td style={{fontWeight:600}}>{p.product_name}</td>
                        <td style={{fontSize:11,color:'var(--tx3)'}}>{p.category}</td>
                        {branches.map((b: string) => { const bd = p.branches[b]; return (<React.Fragment key={b}><td className="td-num" style={{borderLeft:'2px solid var(--bdr)'}}>{bd ? fmt(bd.qty) : '-'}</td><td className="td-num">{bd ? fmt(bd.cost_value) : '-'}</td><td className="td-num" style={{color:'var(--blue)'}}>{bd ? fmt(bd.retail_value) : '-'}</td></React.Fragment>); })}
                        <td className="td-num" style={{borderLeft:'2px solid var(--bdr)',fontWeight:700}}>{fmt(p.total_qty)} {p.unit}</td>
                        <td className="td-num" style={{fontWeight:700}}>{fmt(p.total_cost)}</td>
                        <td className="td-num" style={{fontWeight:700,color:'var(--blue)'}}>{fmt(p.total_retail)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                <tr style={{borderTop:'2px solid var(--bdr)',fontWeight:700,background:'var(--sur2)'}}>
                  <td colSpan={3} style={{padding:'10px 14px'}}>TOTAL</td>
                  {branches.map((b: string) => <React.Fragment key={b}><td style={{borderLeft:'2px solid var(--bdr)'}}></td><td></td><td></td></React.Fragment>)}
                  <td className="td-num" style={{borderLeft:'2px solid var(--bdr)',fontWeight:700}}>{fmt(sum.total_products > 0 ? Object.values(grouped).flat().reduce((s: number, p: any) => s + Number(p.total_qty), 0) : 0)}</td>
                  {canSeeMargin && <td className="td-num">{fmt(sum.total_cost_value)}</td>}
                  <td className="td-num" style={{color:'var(--blue)'}}>{fmt(sum.total_retail_value)}</td>
                </tr>
              </tbody>
            </table></div>
          </>
        );
      })()}

      {/* STOCK ON HAND PIVOT */}
      {!loading && tab==='ip' && data && (() => {
        const branches = (data.data && data.data.branches) ? data.data.branches : [];
        const grouped = (data.data && data.data.grouped) ? data.data.grouped : {};
        return (
          <>
            <div className="kpi-grid" style={{marginBottom:12}}>
              <div className="kpi-card"><div className="kpi-label">Total Products</div><div className="kpi-value" style={{fontSize:18}}>{sum.total_products}</div></div>
              {canSeeMargin && <div className="kpi-card"><div className="kpi-label">Inventory Value</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(sum.total_cost_value)}</div></div>}
              {canSeeMargin && <div className="kpi-card"><div className="kpi-label">Potential Revenue</div><div className="kpi-value" style={{fontSize:18,color:'var(--green)'}}>{fmt(sum.total_retail)}</div></div>}
              {canSeeMargin && <div className="kpi-card"><div className="kpi-label">Potential Profit</div><div className="kpi-value" style={{fontSize:18,color:'var(--green)'}}>{fmt(sum.potential_profit)}</div></div>}
            </div>
            <div className="table-wrap" style={{overflowX:'auto'}}><table>
              <thead>
                <tr>
                  <th rowSpan={2}>SKU</th><th rowSpan={2}>Product</th><th rowSpan={2}>Category</th>
                  {branches.map((b: string) => <th key={b} colSpan={2} style={{textAlign:'center',borderLeft:'2px solid var(--bdr)',color:'var(--blue)'}}>{b}</th>)}
                  <th colSpan={2} style={{textAlign:'center',borderLeft:'2px solid var(--bdr)',background:'var(--sur2)'}}>Total</th>
                </tr>
                <tr>
                  {branches.map((b: string) => (<React.Fragment key={b}><th style={{textAlign:'right',borderLeft:'2px solid var(--bdr)',fontSize:10,color:'var(--tx3)'}}>Qty</th><th style={{textAlign:'right',fontSize:10,color:'var(--tx3)'}}>Unit Cost</th></React.Fragment>))}
                  <th style={{textAlign:'right',borderLeft:'2px solid var(--bdr)',fontSize:10,color:'var(--tx3)',background:'var(--sur2)'}}>Qty</th>
                  <th style={{textAlign:'right',fontSize:10,color:'var(--tx3)',background:'var(--sur2)'}}>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([category, products], gi) => (
                  <React.Fragment key={gi}>
                    <tr style={{background:'var(--sur2)'}}><td colSpan={3+branches.length*2+2} style={{padding:'8px 14px',fontSize:10,fontWeight:700,color:'var(--tx3)',textTransform:'uppercase',letterSpacing:1}}>{category}</td></tr>
                    {(products as any[]).map((p, i) => (
                      <tr key={i}>
                        <td className="td-code">{p.sku}</td>
                        <td style={{fontWeight:600}}>{p.product_name}</td>
                        <td style={{fontSize:11,color:'var(--tx3)'}}>{p.category}</td>
                        {branches.map((b: string) => { const bd = p.branches[b]; return (<React.Fragment key={b}><td className="td-num" style={{borderLeft:'2px solid var(--bdr)'}}>{bd ? fmt(bd.qty) : '-'}</td><td className="td-num" style={{color:'var(--tx3)',fontSize:11}}>{bd ? Number(bd.unit_cost).toLocaleString('en-US',{minimumFractionDigits:4,maximumFractionDigits:4}) : '-'}</td></React.Fragment>); })}
                        <td className="td-num" style={{borderLeft:'2px solid var(--bdr)',fontWeight:700}}>{fmt(p.total_qty)} {p.unit}</td>
                        <td className="td-num" style={{fontWeight:700,color:'var(--blue)'}}>{fmt(p.total_value)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                <tr style={{borderTop:'2px solid var(--bdr)',fontWeight:700,background:'var(--sur2)'}}>
                  <td colSpan={3} style={{padding:'10px 14px'}}>TOTAL</td>
                  {branches.map((b: string) => <React.Fragment key={b}><td style={{borderLeft:'2px solid var(--bdr)'}}></td><td></td></React.Fragment>)}
                  <td className="td-num" style={{borderLeft:'2px solid var(--bdr)'}}>{fmt(sum.total_qty)}</td>
                  {canSeeMargin && <td className="td-num" style={{color:'var(--blue)'}}>{fmt(sum.total_cost_value)}</td>}
                </tr>
              </tbody>
            </table></div>
          </>
        );
      })()}

      
      {/* INVENTORY BY LOCATION PIVOT */}
      {!loading && tab==='sbid' && data && (() => {
        const d = data.data || {};
        const s = d.summary || {};
        const allGroups = d.groups || [];
        const groups = lossesOnly
          ? allGroups.map((g:any) => ({...g, lines: g.lines.filter((l:any) => Number(l.line_profit) < 0)})).filter((g:any) => g.lines.length > 0)
          : allGroups;
        return (
          <div>
            <div className="kpi-grid" style={{marginBottom:12}}>
              <div className="kpi-card"><div className="kpi-label">Products</div><div className="kpi-value" style={{fontSize:18}}>{s.products||0}</div></div>
              <div className="kpi-card"><div className="kpi-label">Lines</div><div className="kpi-value" style={{fontSize:18}}>{s.lines||0}</div></div>
              <div className="kpi-card"><div className="kpi-label">Revenue</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(s.revenue)}</div></div>
              <div className="kpi-card"><div className="kpi-label">Profit</div><div className="kpi-value" style={{fontSize:18,color:Number(s.profit)<0?'var(--red)':'var(--green)'}}>{fmt(s.profit)}</div></div>
            </div>

            <div className="filter-bar" style={{marginBottom:12,display:'flex',alignItems:'center',gap:10}}>
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
                <input type="checkbox" checked={lossesOnly} onChange={e => setLossesOnly(e.target.checked)} />
                Show only lines sold below cost
              </label>
              {Number(s.loss_lines) > 0 && <span style={{fontSize:12,color:'var(--red)'}}>{s.loss_lines} line(s) below cost</span>}
            </div>

            {groups.length === 0 ? <div className="card"><div className="empty-state"><p>{lossesOnly ? 'No lines were sold below cost.' : 'No sales in this period.'}</p></div></div>
            : groups.map((g:any) => (
              <div className="card" key={g.product_id} style={{marginBottom:10}}>
                <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span><span className="td-code">{g.sku}</span> &nbsp; {g.product_name} {g.category_name && <span style={{fontSize:11,color:'var(--tx3)'}}>· {g.category_name}</span>}</span>
                  <span style={{fontSize:12,color: Number(g.profit)<0 ? 'var(--red)' : 'var(--tx3)'}}>
                    Qty {fmt(g.qty)} · Avg price {fmt(g.avg_price)} · Avg cost {fmt(g.avg_cost)} · Revenue {fmt(g.revenue)} · Cost {fmt(g.cost)} · Profit {fmt(g.profit)}
                  </span>
                </div>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead><tr style={{background:'var(--sur2)'}}>
                    <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Date</th>
                    <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Document</th>
                    <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Customer</th>
                    <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Location</th>
                    <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Qty</th>
                    <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Price</th>
                    <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Unit Cost</th>
                    <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Total</th>
                    <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Profit</th>
                  </tr></thead>
                  <tbody>
                    {g.lines.map((l:any, i:number) => {
                      const loss = Number(l.line_profit) < 0;
                      return (
                        <tr key={i} style={{borderBottom:'1px solid var(--bdr)'}}>
                          <td style={{padding:'7px 10px',fontSize:11,color:'var(--tx3)'}}>{l.sale_date ? new Date(l.sale_date).toLocaleDateString() : '-'}</td>
                          <td className="td-code" style={{padding:'7px 10px'}}>{l.sale_no}</td>
                          <td style={{padding:'7px 10px',fontSize:12}}>{l.customer_name || 'Walk-in'}</td>
                          <td style={{padding:'7px 10px',fontSize:11,color:'var(--tx3)'}}>{l.location_name}</td>
                          <td className="td-num" style={{padding:'7px 10px'}}>{fmt(l.qty)}</td>
                          <td className="td-num" style={{padding:'7px 10px'}}>{fmt(l.unit_price)}</td>
                          <td className="td-num" style={{padding:'7px 10px',color: loss ? 'var(--red)' : 'var(--tx3)'}}>{Number(l.unit_cost).toLocaleString('en-US',{minimumFractionDigits:4,maximumFractionDigits:4})}</td>
                          <td className="td-num" style={{padding:'7px 10px'}}>{fmt(l.line_total)}</td>
                          <td className="td-num" style={{padding:'7px 10px',fontWeight:600,color: loss ? 'var(--red)' : 'var(--green)'}}>{fmt(l.line_profit)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        );
      })()}

      {!loading && tab==='sbi' && data && (() => {
        const d = data.data || {};
        const s = d.summary || {};
        const all = d.rows || [];
        const rows = lossesOnly ? all.filter((r:any) => Number(r.profit) < 0) : all;
        return (
          <div>
            <div className="kpi-grid" style={{marginBottom:12}}>
              <div className="kpi-card"><div className="kpi-label">Products sold</div><div className="kpi-value" style={{fontSize:18}}>{s.products||0}</div></div>
              <div className="kpi-card"><div className="kpi-label">Units</div><div className="kpi-value" style={{fontSize:18}}>{fmt(s.qty)}</div></div>
              <div className="kpi-card"><div className="kpi-label">Revenue</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(s.revenue)}</div></div>
              <div className="kpi-card"><div className="kpi-label">Profit</div><div className="kpi-value" style={{fontSize:18,color:Number(s.profit)<0?'var(--red)':'var(--green)'}}>{fmt(s.profit)}</div></div>
            </div>

            <div className="filter-bar" style={{marginBottom:12,display:'flex',alignItems:'center',gap:10}}>
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer'}}>
                <input type="checkbox" checked={lossesOnly} onChange={e => setLossesOnly(e.target.checked)} />
                Show only items sold below cost
              </label>
              {Number(s.losses) > 0 && <span style={{fontSize:12,color:'var(--red)'}}>{s.losses} item(s) sold below cost</span>}
              <span className="record-count" style={{marginLeft:'auto'}}>{rows.length} of {all.length}</span>
            </div>

            <div className="card">
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr style={{background:'var(--sur2)'}}>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>SKU</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Product</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Category</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Qty Sold</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Returned</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Avg price</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Avg cost</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Revenue</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Cost</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Profit</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Margin</th>
                </tr></thead>
                <tbody>
                  {rows.length===0 ? <tr><td colSpan={9} style={{textAlign:'center',padding:24,color:'var(--tx3)'}}>{lossesOnly ? 'No items were sold below cost.' : 'No sales in this period.'}</td></tr>
                  : rows.map((r:any) => {
                    const loss = Number(r.profit) < 0;
                    return (
                      <tr key={r.product_id} style={{borderBottom:'1px solid var(--bdr)',background: loss ? 'var(--sur2)' : undefined}}>
                        <td className="td-code" style={{padding:'8px 10px'}}>{r.sku}</td>
                        <td style={{padding:'8px 10px',fontSize:12}}>{r.product_name}</td>
                        <td style={{padding:'8px 10px',fontSize:12,color:'var(--tx3)'}}>{r.category_name || '-'}</td>
                        <td className="td-num" style={{padding:'8px 10px'}}>{fmt(r.qty_sold)} {r.unit}</td>
                        <td className="td-num" style={{padding:'8px 10px',color:'var(--tx3)',fontSize:11}}>{Number(r.qty_returned) > 0 ? fmt(r.qty_returned) : '-'}</td>
                        <td className="td-num" style={{padding:'8px 10px'}}>{fmt(r.avg_price)}</td>
                        <td className="td-num" style={{padding:'8px 10px',color:'var(--tx3)'}}>{fmt(r.avg_cost)}</td>
                        <td className="td-num" style={{padding:'8px 10px'}}>{fmt(r.revenue)}</td>
                        <td className="td-num" style={{padding:'8px 10px',color:'var(--tx3)'}}>{fmt(r.cost)}</td>
                        <td className="td-num" style={{padding:'8px 10px',fontWeight:600,color: loss ? 'var(--red)' : 'var(--green)'}}>{fmt(r.profit)}</td>
                        <td className="td-num" style={{padding:'8px 10px',color: loss ? 'var(--red)' : 'var(--tx2)'}}>{r.margin}%</td>
                      </tr>
                    );
                  })}
                </tbody>
                {rows.length > 0 && (
                  <tfoot><tr style={{background:'var(--sur2)',fontWeight:700}}>
                    <td colSpan={3} style={{padding:'8px 10px'}}>Total</td>
                    <td className="td-num" style={{padding:'8px 10px'}}>{fmt(rows.reduce((a:number,r:any)=>a+Number(r.qty_sold),0))}</td>
                    <td className="td-num" style={{padding:'8px 10px'}}>{fmt(rows.reduce((a:number,r:any)=>a+Number(r.qty_returned),0))}</td>
                    <td></td><td></td>
                    <td className="td-num" style={{padding:'8px 10px'}}>{fmt(rows.reduce((a:number,r:any)=>a+Number(r.revenue),0))}</td>
                    <td className="td-num" style={{padding:'8px 10px'}}>{fmt(rows.reduce((a:number,r:any)=>a+Number(r.cost),0))}</td>
                    <td className="td-num" style={{padding:'8px 10px',color:'var(--green)'}}>{fmt(rows.reduce((a:number,r:any)=>a+Number(r.profit),0))}</td>
                    <td></td>
                  </tr></tfoot>
                )}
              </table>
            </div>
          </div>
        );
      })()}

      {!loading && tab==='exp' && data && (() => {
        const d = data.data || {};
        const s = d.summary || {};
        const batches = d.batches || [];
        const unbatched = d.unbatched || [];
        const stateStyle = (st:string) => st==='expired'
          ? { background:'#FCEBEB', color:'#791F1F' }
          : st==='expiring' ? { background:'#FAEEDA', color:'#633806' }
          : { background:'var(--sur2)', color:'var(--tx3)' };
        return (
          <div>
            <div className="kpi-grid" style={{marginBottom:12}}>
              <div className="kpi-card"><div className="kpi-label">Expired batches</div><div className="kpi-value" style={{color:'var(--red)'}}>{s.expired_count||0}</div><div className="kpi-sub">Value {fmt(s.expired_value)}</div></div>
              <div className="kpi-card"><div className="kpi-label">Expiring in {s.days_window||90} days</div><div className="kpi-value" style={{color:'var(--amber)'}}>{s.expiring_count||0}</div><div className="kpi-sub">Value {fmt(s.expiring_value)}</div></div>
              <div className="kpi-card"><div className="kpi-label">Untracked lines</div><div className="kpi-value">{s.unbatched_lines||0}</div><div className="kpi-sub">No batch recorded</div></div>
            </div>

            <div className="card" style={{marginBottom:12}}>
              <div className="card-header"><span>Tracked batches</span><span className="record-count">{batches.length}</span></div>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr style={{background:'var(--sur2)'}}>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>SKU</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Product</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Batch</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Location</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Expiry</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Remaining</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Value</th>
                </tr></thead>
                <tbody>
                  {batches.length===0 ? <tr><td colSpan={7} style={{textAlign:'center',padding:24,color:'var(--tx3)'}}>No tracked batches yet. Batches are created when a GRN with batch and expiry details is confirmed.</td></tr>
                  : batches.map((b:any) => (
                    <tr key={b.id} style={{borderBottom:'1px solid var(--bdr)'}}>
                      <td className="td-code" style={{padding:'8px 10px'}}>{b.sku}</td>
                      <td style={{padding:'8px 10px',fontSize:12}}>{b.product_name}</td>
                      <td style={{padding:'8px 10px',fontSize:12,fontFamily:'monospace'}}>{b.batch_no||'-'}</td>
                      <td style={{padding:'8px 10px',fontSize:12,color:'var(--tx3)'}}>{b.location_name}</td>
                      <td style={{padding:'8px 10px',fontSize:12}}>
                        <span style={{...stateStyle(b.state),padding:'2px 8px',borderRadius:4,fontSize:11}}>
                          {b.expiry_date ? new Date(b.expiry_date).toLocaleDateString() : 'None'}
                          {b.days_to_expiry!==null && b.days_to_expiry!==undefined ? (b.days_to_expiry<0 ? ' · expired' : ' · ' + b.days_to_expiry + 'd') : ''}
                        </span>
                      </td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontFamily:'monospace',fontSize:12}}>{Number(b.qty_remaining)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontFamily:'monospace',fontSize:12}}>{fmt(b.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card">
              <div className="card-header"><span>Stock without batch details</span><span className="record-count">{unbatched.length}</span></div>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr style={{background:'var(--sur2)'}}>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>SKU</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Product</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Location</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>On hand</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Batched</th>
                  <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Untracked</th>
                </tr></thead>
                <tbody>
                  {unbatched.length===0 ? <tr><td colSpan={6} style={{textAlign:'center',padding:24,color:'var(--tx3)'}}>All expiry-tracked stock has batch details.</td></tr>
                  : unbatched.map((u:any,i:number) => (
                    <tr key={i} style={{borderBottom:'1px solid var(--bdr)'}}>
                      <td className="td-code" style={{padding:'8px 10px'}}>{u.sku}</td>
                      <td style={{padding:'8px 10px',fontSize:12}}>{u.product_name}</td>
                      <td style={{padding:'8px 10px',fontSize:12,color:'var(--tx3)'}}>{u.location_name}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontFamily:'monospace',fontSize:12}}>{Number(u.qty_on_hand)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontFamily:'monospace',fontSize:12,color:'var(--tx3)'}}>{Number(u.batched_qty)}</td>
                      <td style={{padding:'8px 10px',textAlign:'right',fontFamily:'monospace',fontSize:12,fontWeight:600}}>{Number(u.unbatched_qty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {!loading && tab==='iloc' && data && (() => {
        const locations = (data.data && data.data.locations) ? data.data.locations : [];
        const grouped = (data.data && data.data.grouped) ? data.data.grouped : {};
        return (
          <>
            <div className="kpi-grid" style={{marginBottom:12}}>
              <div className="kpi-card"><div className="kpi-label">Total Products</div><div className="kpi-value" style={{fontSize:18}}>{sum.total_products}</div></div>
              <div className="kpi-card"><div className="kpi-label">Total Qty</div><div className="kpi-value" style={{fontSize:18}}>{fmt(sum.total_qty)}</div></div>
              {canSeeMargin && <div className="kpi-card"><div className="kpi-label">Inventory Value</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(sum.total_cost_value)}</div></div>}
            </div>
            <div className="table-wrap" style={{overflowX:'auto'}}><table>
              <thead>
                <tr>
                  <th rowSpan={2}>SKU</th><th rowSpan={2}>Product</th><th rowSpan={2}>Category</th>
                  {locations.map((l: string) => <th key={l} colSpan={2} style={{textAlign:'center',borderLeft:'2px solid var(--bdr)',color:'var(--blue)',fontSize:10}}>{l}</th>)}
                  <th colSpan={2} style={{textAlign:'center',borderLeft:'2px solid var(--bdr)',background:'var(--sur2)'}}>Total</th>
                </tr>
                <tr>
                  {locations.map((l: string) => (<React.Fragment key={l}><th style={{textAlign:'right',borderLeft:'2px solid var(--bdr)',fontSize:10,color:'var(--tx3)'}}>Qty</th><th style={{textAlign:'right',fontSize:10,color:'var(--tx3)'}}>Cost</th></React.Fragment>))}
                  <th style={{textAlign:'right',borderLeft:'2px solid var(--bdr)',fontSize:10,background:'var(--sur2)'}}>Qty</th>
                  <th style={{textAlign:'right',fontSize:10,background:'var(--sur2)'}}>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([category, products], gi) => (
                  <React.Fragment key={gi}>
                    <tr style={{background:'var(--sur2)'}}><td colSpan={3+locations.length*2+2} style={{padding:'8px 14px',fontSize:10,fontWeight:700,color:'var(--tx3)',textTransform:'uppercase',letterSpacing:1}}>{category}</td></tr>
                    {(products as any[]).map((p, i) => (
                      <tr key={i}>
                        <td className="td-code">{p.sku}</td>
                        <td style={{fontWeight:600}}>{p.product_name}</td>
                        <td style={{fontSize:11,color:'var(--tx3)'}}>{p.category}</td>
                        {locations.map((l: string) => { const ld = p.locations[l]; return (<React.Fragment key={l}><td className="td-num" style={{borderLeft:'2px solid var(--bdr)'}}>{ld ? fmt(ld.qty) : '-'}</td><td className="td-num" style={{color:'var(--tx3)',fontSize:11}}>{ld ? fmt(ld.unit_cost) : '-'}</td></React.Fragment>); })}
                        <td className="td-num" style={{borderLeft:'2px solid var(--bdr)',fontWeight:700}}>{fmt(p.total_qty)} {p.unit}</td>
                        <td className="td-num" style={{fontWeight:700,color:'var(--blue)'}}>{fmt(p.total_value)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                <tr style={{borderTop:'2px solid var(--bdr)',fontWeight:700,background:'var(--sur2)'}}>
                  <td colSpan={3} style={{padding:'10px 14px'}}>TOTAL</td>
                  {locations.map((l: string) => <React.Fragment key={l}><td style={{borderLeft:'2px solid var(--bdr)'}}></td><td></td></React.Fragment>)}
                  <td className="td-num" style={{borderLeft:'2px solid var(--bdr)'}}>{fmt(sum.total_qty)}</td>
                  {canSeeMargin && <td className="td-num" style={{color:'var(--blue)'}}>{fmt(sum.total_cost_value)}</td>}
                </tr>
              </tbody>
            </table></div>
          </>
        );
      })()}

      {/* Inventory Activity */}
      {!loading && tab==='usm' && data && (() => {
        const rows = Array.isArray(data.data)?data.data:[]; const s = data.summary||{};
        return (<>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Dead Items</div><div className="kpi-value" style={{fontSize:18}}>{s.products||0}</div></div>
            <div className="kpi-card"><div className="kpi-label">Value Tied Up</div><div className="kpi-value" style={{fontSize:18,color:'var(--red)'}}>{fmt(s.total_value)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Period</div><div className="kpi-value" style={{fontSize:12}}>{s.from} to {s.to}</div></div>
          </div>
          <div className="table-wrap"><table>
            <thead><tr><th>Branch</th><th>Location</th><th>SKU</th><th>Product</th><th style={{textAlign:'right'}}>On Hand</th><th style={{textAlign:'right'}}>Unit Cost</th><th style={{textAlign:'right'}}>Value</th><th>Last Moved</th><th style={{textAlign:'right'}}>Days Idle</th></tr></thead>
            <tbody>{rows.length===0?<tr><td colSpan={9}><div className="empty-state"><p>No un-moved stock in this period.</p></div></td></tr>:rows.map((r:any,i:number) => <tr key={i}><td style={{fontSize:11,color:'var(--tx3)'}}>{r.branch}</td><td style={{fontSize:11,color:'var(--tx3)'}}>{r.location}</td><td className="td-code">{r.sku}</td><td style={{fontWeight:600}}>{r.product_name}</td><td className="td-num">{fmt(r.qty_on_hand)}</td><td className="td-num">{fmt(r.cost_price)}</td><td className="td-num" style={{fontWeight:700}}>{fmt(r.total_value)}</td><td style={{fontSize:11,color:'var(--tx3)'}}>{r.last_moved?new Date(r.last_moved).toLocaleDateString():'never'}</td><td className="td-num" style={{color:'var(--red)',fontWeight:700}}>{r.days_idle==null?'-':r.days_idle}</td></tr>)}</tbody>
          </table></div>
        </>);
      })()}

      {!loading && tab==='sbc' && data && (() => {
        const d = data.data||{}; const rows = Array.isArray(d.rows)?d.rows:[]; const s = d.summary||{};
        return (<>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Customers</div><div className="kpi-value" style={{fontSize:18}}>{s.customers||0}</div></div>
            <div className="kpi-card"><div className="kpi-label">Invoices</div><div className="kpi-value" style={{fontSize:18}}>{s.invoices||0}</div></div>
            <div className="kpi-card"><div className="kpi-label">Revenue</div><div className="kpi-value" style={{fontSize:18,color:'var(--blue)'}}>{fmt(s.revenue)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Profit</div><div className="kpi-value" style={{fontSize:18,color:'var(--green)'}}>{fmt(s.profit)}</div></div>
          </div>
          <div className="table-wrap"><table>
            <thead><tr><th>Customer</th><th style={{textAlign:'right'}}>Invoices</th><th style={{textAlign:'right'}}>Qty</th><th style={{textAlign:'right'}}>Revenue</th><th style={{textAlign:'right'}}>Cost</th><th style={{textAlign:'right'}}>Profit</th><th style={{textAlign:'right'}}>Margin %</th><th style={{textAlign:'right'}}>Owes Now</th><th></th></tr></thead>
            <tbody>{rows.length===0?<tr><td colSpan={9}><div className="empty-state"><p>No sales in this period.</p></div></td></tr>:rows.map((r:any,i:number) => <tr key={i}><td style={{fontWeight:600}}>{r.customer_name}</td><td className="td-num">{r.invoices}</td><td className="td-num">{fmt(r.qty)}</td><td className="td-num" style={{fontWeight:700,color:'var(--blue)'}}>{fmt(r.revenue)}</td><td className="td-num">{fmt(r.cost)}</td><td className="td-num" style={{color:Number(r.profit)<0?'var(--red)':'var(--green)',fontWeight:700}}>{fmt(r.profit)}</td><td className="td-num">{fmt(r.margin)}</td><td className="td-num" style={{color:Number(r.balance)>0?'var(--red)':'var(--tx3)'}}>{fmt(r.balance)}</td><td><button onClick={() => { setContactId(r.contact_id); setTab('sbcd'); }} style={{fontSize:10,padding:'2px 8px',border:'1px solid var(--bdr)',borderRadius:4,background:'transparent',color:'var(--blue)',cursor:'pointer'}}>Detail</button></td></tr>)}</tbody>
          </table></div>
        </>);
      })()}

      {!loading && tab==='sbcd' && data && (() => {
        const customers = data.data?.customers || [];
        const s2 = data.summary || {};
        const money = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
        const qtyFmt = (n:any) => Number(n||0).toLocaleString('en-US',{maximumFractionDigits:2});
        return (
          <div className="card" style={{padding:0}}>
            <table>
              <thead><tr>
                <th>Date</th><th>Invoice #</th><th>Item</th>
                <th style={{textAlign:'right'}}>Qty</th><th style={{textAlign:'right'}}>Unit Price</th>
                <th style={{textAlign:'right'}}>Amount</th><th style={{textAlign:'right'}}>Cumulative</th>
              </tr></thead>
              <tbody>
                {customers.length===0 ? <tr><td colSpan={7}><div className="empty-state"><p>No sales in this period.</p></div></td></tr>
                : customers.map((c:any) => {
                  const show = !collapsed['s'+c.contact_id];
                  return (
                    <React.Fragment key={c.contact_id}>
                      <tr className="qb-group" style={{cursor:'pointer'}} onClick={() => setCollapsed(st => ({...st, ['s'+c.contact_id]: !st['s'+c.contact_id]}))}>
                        <td colSpan={5}>
                          <span style={{display:'inline-block',width:14,color:'var(--tx3)'}}>{show?'▾':'▸'}</span>
                          {c.customer_name}
                        </td>
                        <td className="td-num">{money(c.total_amount)}</td>
                        <td className="td-num"></td>
                      </tr>
                      {show && (<>
                        {c.lines.map((l:any,i:number) => (
                          <tr key={i}>
                            <td style={{fontSize:11,color:'var(--tx3)'}}>{l.date?new Date(l.date).toLocaleDateString():''}</td>
                            <td className="td-code">{l.sale_no}</td>
                            <td style={{fontSize:12}}>{l.item}{l.sku?<span style={{color:'var(--tx3)',fontSize:11}}> · {l.sku}</span>:''}</td>
                            <td className="td-num">{qtyFmt(l.qty)}</td>
                            <td className="td-num">{money(l.unit_price)}</td>
                            <td className="td-num">{money(l.amount)}</td>
                            <td className="td-num" style={{color:'var(--tx3)'}}>{money(l.cumulative)}</td>
                          </tr>
                        ))}
                        <tr className="qb-sub"><td colSpan={3}>Subtotal — {c.customer_name}</td><td className="td-num">{qtyFmt(c.total_qty)}</td><td></td><td className="td-num">{money(c.total_amount)}</td><td></td></tr>
                      </>)}
                    </React.Fragment>
                  );
                })}
                {customers.length>0 && (
                  <tr className="qb-grand"><td colSpan={3}>GRAND TOTAL</td><td className="td-num">{qtyFmt(s2.grand_qty)}</td><td></td><td className="td-num">{money(s2.grand_amount)}</td><td></td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })()}

      {!loading && tab==='ia' && data && (
        <>
          <div className="kpi-grid" style={{marginBottom:12}}>
            <div className="kpi-card"><div className="kpi-label">Total In</div><div className="kpi-value" style={{fontSize:18,color:'var(--green)'}}>{fmt(sum.total_in)}</div></div>
            <div className="kpi-card"><div className="kpi-label">Total Out</div><div className="kpi-value" style={{fontSize:18,color:'var(--red)'}}>{fmt(sum.total_out)}</div></div>
          </div>
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>SKU</th><th>Product</th><th>Type</th><th>From</th><th>To</th><th>Contact</th><th style={{textAlign:'right'}}>Qty</th><th style={{textAlign:'right'}}>Unit Cost</th><th style={{textAlign:'right'}}>Cost Impact</th></tr></thead>
            <tbody>{tb.length===0?<tr><td colSpan={10}><div className="empty-state"><p>No activity.</p></div></td></tr>:tb.map((r:any,i:number) => <tr key={i}><td style={{fontSize:11,color:'var(--tx3)'}}>{new Date(r.created_at).toLocaleDateString()}</td><td className="td-code">{r.sku}</td><td style={{fontWeight:600}}>{r.product_name}</td><td><span className={`badge ${Number(r.qty)>0?'badge-green':'badge-red'}`}>{r.move_type}</span></td><td style={{fontSize:11,color:'var(--tx3)'}}>{r.from_location||'-'}</td><td style={{fontSize:11,color:'var(--tx3)'}}>{r.to_location||'-'}</td><td style={{fontSize:11,color:'var(--tx3)'}}>{r.contact_name||'-'}</td><td className="td-num" style={{color:Number(r.qty)>0?'var(--green)':'var(--red)',fontWeight:700}}>{Number(r.qty)>0?'+':''}{fmt(r.qty)}</td><td className="td-num">{fmt(r.unit_cost)}</td><td className="td-num" style={{color:Number(r.total_cost_impact)>0?'var(--green)':'var(--red)'}}>{fmt(r.total_cost_impact)}</td></tr>)}</tbody>
          </table></div>
        </>
      )}
      </div>
    </>
  );
};

export default Reports;




